import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogSelectTrigger,
  DialogTitle,
} from "@/lib/components/Dialog";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { usePagedTeamFontsQuery } from "@/lib/queries/font-queries";
import { FontDto, FontStyleDto } from "@repo/api/font";
import { getFontWeights, getFontStyles } from "@repo/api/utils/font";
import { IconMail } from "@tabler/icons-react";
import { showNewMessage } from "@intercom/messenger-js-sdk";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/lib/components/Button/Button";
import { FontDialogTab, FontTabs } from "./FontTabs";
import {
  FontFilters,
  FontOwnershipFilter,
  FontCategoryFilter,
} from "./FontFilters";
import FontListView from "./FontListView";
import DeleteFontDialog from "./DeleteFontDialog";
import { pickClosestWeight, pickStyle } from "./font-utils";

const SEARCH_DEBOUNCE_MS = 300;

type Props = {
  selectedFamily: string;
  selectedExternalId?: string;
  currentWeight: number;
  currentStyle: FontStyleDto;
  onSelectFont: (params: {
    font: FontDto;
    fontWeight: number;
    fontStyle: FontStyleDto;
  }) => void;
};

export default function FontDialog({
  selectedFamily,
  selectedExternalId,
  currentWeight,
  currentStyle,
  onSelectFont,
}: Props) {
  const view = useEditorStore((state) => state.fontDialogState.view);
  const setFontDialogView = useEditorStore(
    (state) => state.service.setFontDialogView,
  );
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const addCompositionFont = useEditorStore(
    (state) => state.service.addCompositionFont,
  );

  const [activeTab, setActiveTab] = useState<FontDialogTab>("ALL_FONTS");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [ownership, setOwnership] = useState<FontOwnershipFilter>("ALL");
  const [category, setCategory] = useState<FontCategoryFilter>("ALL");
  const [fontToDelete, setFontToDelete] = useState<FontDto | null>(null);

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const {
    data: pagedFontsData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = usePagedTeamFontsQuery(
    teamExternalId,
    {
      search: debouncedSearch,
      ownership,
      category: category === "ALL" ? undefined : category,
    },
    { enabled: view !== "HIDDEN" },
  );

  const fonts = useMemo(
    () => pagedFontsData?.pages.flatMap((page) => page.fonts) ?? [],
    [pagedFontsData],
  );

  useEffect(() => {
    if (view === "FORM") {
      setActiveTab("ADD_FONT");
    } else if (view === "LIST") {
      setActiveTab("ALL_FONTS");
    }
  }, [view]);

  useEffect(() => {
    if (view === "HIDDEN") {
      setSearchTerm("");
      setDebouncedSearch("");
      setOwnership("ALL");
      setCategory("ALL");
    }
  }, [view]);

  const applyFont = (font: FontDto) => {
    const fontWeight = pickClosestWeight(
      getFontWeights(font.metadata),
      currentWeight,
    );
    const fontStyle = pickStyle(
      getFontStyles(font.metadata, fontWeight),
      currentStyle,
    );

    // Keep the selected font available to the preview's FontContext, since the
    // composition only carries the fonts it references.
    addCompositionFont(font);

    onSelectFont({ font, fontWeight, fontStyle });
  };

  const handleUseFont = (font: FontDto) => {
    applyFont(font);
    setFontDialogView("HIDDEN");
  };

  const handleDeleteFont = (font: FontDto) => {
    setFontToDelete(font);
  };

  const handleTabChange = (tab: FontDialogTab) => {
    setActiveTab(tab);
    setFontDialogView(tab === "ALL_FONTS" ? "LIST" : "FORM");
  };

  return (
    <Dialog
      open={view !== "HIDDEN"}
      onOpenChange={(open) => setFontDialogView(open ? "LIST" : "HIDDEN")}
    >
      <DialogSelectTrigger className="h-9">
        {selectedFamily || "Select font"}
      </DialogSelectTrigger>
      <DialogContent
        className="mx-auto h-[calc(100%-48px)] max-h-196 w-[calc(100%-48px)] max-w-273 overflow-hidden border-none"
        classNameInner="relative flex min-h-0 flex-col gap-0 overflow-hidden p-0 sm:px-0"
        showCloseButton={false}
      >
        <DialogHeader variant="bordered" showCloseButton className="px-8 py-6">
          <DialogTitle>Fonts</DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 flex-1">
          <aside className="flex w-71.25 flex-col border-r border-grey-200">
            <FontTabs activeTab={activeTab} onTabChange={handleTabChange} />
            {activeTab === "ALL_FONTS" && (
              <FontFilters
                ownership={ownership}
                onOwnershipChange={setOwnership}
                category={category}
                onCategoryChange={setCategory}
              />
            )}
          </aside>
          <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
            {activeTab === "ALL_FONTS" ? (
              <FontListView
                fonts={fonts}
                searchQuery={searchTerm}
                onSearchChange={setSearchTerm}
                selectedExternalId={selectedExternalId}
                onUseFont={handleUseFont}
                onDeleteFont={handleDeleteFont}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                isLoading={isLoading}
                onLoadMore={fetchNextPage}
              />
            ) : (
              <ContactSupportMessage />
            )}
          </div>
        </div>
      </DialogContent>
      <DeleteFontDialog
        font={fontToDelete}
        open={fontToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setFontToDelete(null);
        }}
      />
    </Dialog>
  );
}

function ContactSupportMessage() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
      <div className="mb-3 [&_svg]:stroke-[url(#icon-gradient)]">
        <IconMail stroke={1.5} size={32} />
      </div>
      <h4 className="mb-1 text-base font-semibold text-text-primary">
        Need custom fonts?
      </h4>
      <p className="mt-1 max-w-md text-sm text-text-secondary">
        Adding fonts for your team is handled by the Amplifiles team. Please get
        in touch with support and we&apos;ll set up your custom fonts for you.
      </p>
      <Button
        variant="default"
        className="mt-5"
        onClick={() =>
          showNewMessage("Hi! I'd like to add custom fonts for my team.")
        }
      >
        Contact support
      </Button>
    </div>
  );
}
